import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getQueryFn, setAuthToken, setJustLoggedIn } from "./queryClient";

interface User {
  id: string;
  username: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Local state to track authenticated user - avoids race condition with query cache
  const [localUser, setLocalUser] = useState<User | null>(null);
  
  const { data, isLoading } = useQuery<{ user: User } | null>({
    queryKey: ["/api/auth/me"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    staleTime: Infinity,
  });
  
  // Clear localUser and JWT when auth check returns null (401)
  // This ensures the UI reflects the true auth state after server restart
  useEffect(() => {
    if (!isLoading && data === null && localUser !== null) {
      setLocalUser(null);
      setAuthToken(null);
    }
  }, [data, isLoading, localUser]);

  const loginMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      const res = await apiRequest("POST", "/api/auth/login", { username, password });
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.user) {
        queryClient.setQueryData(["/api/auth/me"], { user: data.user });
        setLocalUser(data.user);
      }
      if (data?.token) {
        setAuthToken(data.token);
      }
      setJustLoggedIn();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/auth/logout");
    },
    onSuccess: () => {
      // Clear JWT token
      setAuthToken(null);
      setLocalUser(null);
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  const login = useCallback(async (username: string, password: string) => {
    const result = await loginMutation.mutateAsync({ username, password });
    if (!result?.user) {
      throw new Error("Innlogging feilet");
    }
  }, [loginMutation]);

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  // Use local user if set (from login), otherwise use query data
  const user = localUser || data?.user || null;
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
